import React from 'react';
import { getSalesShippingSnapshot, getShippingSnapshotAddress } from './utils/shippingSnapshot.js';

const formatAddress = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  return `${addr.address || ''}${addr.city ? ', ' + addr.city : ''}${addr.state ? ', ' + addr.state : ''}${addr.pincode ? ' - ' + addr.pincode : ''}`.trim();
};

const renderPrintCheckbox = () => (
  <span
    style={{
      display: 'inline-block',
      width: '12px',
      height: '12px',
      border: '1px solid #000',
      marginLeft: '8px',
      verticalAlign: 'middle',
    }}
  />
);

const formatDateTime = (value) => {
  if (!value) return '';

  const dateValue = typeof value?.toDate === 'function'
    ? value.toDate()
    : new Date(value);

  return Number.isNaN(dateValue.getTime()) ? '' : dateValue.toLocaleString();
};

const formatWeight = (value) => {
  const numericValue = Number.parseFloat(value);
  if (Number.isNaN(numericValue)) return value || '0.00';
  return numericValue.toFixed(2);
};

const getSalesManagerSignatureSrc = (name) => {
  switch (name) {
    case 'Rohit Soni':
      return '/Rohit-Soni-Sign.png';
    case 'Rajesh Soni':
      return '/Rajesh-Soni-Sign.png';
    case 'Sanjay Kumawat':
      return '/Sanjay-Kumawat-Sign.png';
    default:
      return '';
  }
};

const MOCPreviewContent = ({
  mocData,
  sourceWarehouse = null,
  destinationWarehouse = null,
  printRef,
  className = 'print-area',
  style = pdfContainerStyle,
  showFinanceSection = true,
  showPostInstructionsSection = true,
}) => {
  if (!mocData) return null;

  const salesShippingSnapshot = mocData?.typeOfMOC === 'Sales' || mocData?.typeOfMOC === 'Rental'
    ? getSalesShippingSnapshot(mocData)
    : null;

  const previewSourceWarehouseName =
    mocData?.warehouse ||
    sourceWarehouse?.name ||
    sourceWarehouse?.sourceName ||
    mocData?.sourceWarehouse ||
    mocData?.sourceName ||
    '---';

  const previewConsigneeName = mocData?.typeOfMOC === 'Interbranch Transfer'
    ? (mocData?.destinationWarehouseName || destinationWarehouse?.name || mocData?.customerName || '---')
    : (salesShippingSnapshot?.customerName || mocData?.customerName || '---');

  const previewShippingAddress = mocData?.typeOfMOC === 'Interbranch Transfer'
    ? (mocData?.destinationWarehouseAddress || (destinationWarehouse ? formatAddress(destinationWarehouse) : (mocData?.shippingAddress || '---')))
    : (getShippingSnapshotAddress(salesShippingSnapshot, mocData?.shippingAddress || '---'));

  const referenceNumberLabel = mocData?.typeOfMOC === 'Interbranch Transfer'
    ? 'Transfer No.'
    : mocData?.typeOfMOC === 'Rental'
      ? 'WO Number'
      : 'PO Number';

  const referenceDateLabel = mocData?.typeOfMOC === 'Interbranch Transfer'
    ? 'Transfer req. date'
    : mocData?.typeOfMOC === 'Rental'
      ? 'WO Date'
      : 'PO DATE';

  const tableTotals = (mocData?.components || []).reduce((acc, component) => {
    acc.newQty += parseInt(component.newQty || 0, 10);
    acc.rentalQty += parseInt(component.rentalQty || 0, 10);
    acc.totalWeight += ((parseInt(component.newQty || 0, 10) + parseInt(component.rentalQty || 0, 10)) * parseFloat(component.weight || 0));
    return acc;
  }, { newQty: 0, rentalQty: 0, totalWeight: 0 });
  const formattedSalesManagerApprovalTime = formatDateTime(mocData.salesManagerApprovedAt);
  const salesManagerName = mocData.salesManagerApprovedBy || mocData.salesReviewedBy || mocData.approvedBySalesperson || '';
  const salesManagerSignatureSrc = getSalesManagerSignatureSrc(salesManagerName);
  const salespersonSignatureSrc = getSalesManagerSignatureSrc(mocData.approvedBySalesperson);
  const opsApprovalTime = mocData.opsApprovedAt || (mocData.opsReviewStatus === 'approved' ? mocData.opsReviewedAt : '');
  const formattedOpsApprovalTime = formatDateTime(opsApprovalTime);
  const formattedFinanceApprovalTime = formatDateTime(mocData.financeApprovedAt);

  return (
    <div ref={printRef} style={style} className={className}>
      <table style={headerTableStyle}>
        <tbody>
          <tr><td colSpan="5" style={{ ...headerTableCellStyle, textAlign: 'center', backgroundColor: '#f1f5f9', fontWeight: '800', fontSize: '18px' }}>PACKAGING/BILL OF MATERIAL</td></tr>
          <tr>
            <td style={labelCellStyle}>Document date</td><td style={valueCellStyle}>{mocData.mocDate}</td>
            <td style={labelCellStyle}>Document No.</td><td style={valueCellStyle}>{mocData.mocNo}</td>
            <td style={valueCellStyle}><strong>Order No:</strong> {mocData.orderNumber || '---'}</td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Type of MOC</td><td style={valueCellStyle}>{mocData.typeOfMOC}</td>
            <td style={labelCellStyle}>Document type</td><td style={valueCellStyle}>Delivery</td>
            <td style={{ ...valueCellStyle, backgroundColor: '#fdf2f2' }}><strong>ALW:</strong> {formatWeight(mocData.aluminiumWeight)} kg</td>
          </tr>
          <tr>
            <td style={labelCellStyle}>{referenceNumberLabel}</td><td style={valueCellStyle}>{mocData.poNumber}</td>
            <td style={labelCellStyle}>{referenceDateLabel}</td><td style={valueCellStyle}>{mocData.poDate}</td>
            <td style={valueCellStyle}><strong>Warehouse:</strong> {previewSourceWarehouseName}</td>
          </tr>
          <tr>
            <td style={labelCellStyle}>Consignee</td><td colSpan="3" style={{ ...valueCellStyle, fontWeight: 'bold' }}>{previewConsigneeName}</td>
            <td rowSpan="4" style={{ ...headerTableCellStyle, textAlign: 'center', verticalAlign: 'middle', width: '120px' }}>
              <strong>Total Meter</strong><br /><span style={{ fontSize: '30px', fontWeight: '900' }}>{mocData.totalMeterNumber}</span>
            </td>
          </tr>
          <tr><td style={labelCellStyle}>Shipping Address</td><td colSpan="3" style={{ ...valueCellStyle, height: '60px', verticalAlign: 'top' }}>{previewShippingAddress}</td></tr>
          <tr><td style={labelCellStyle}>Material Code</td><td colSpan="3" style={valueCellStyle}>{mocData.materialCode || '---'}</td></tr>
          <tr><td style={labelCellStyle}>Product Summary</td><td colSpan="3" style={valueCellStyle}>{mocData.productSummary}</td></tr>
        </tbody>
      </table>

      <table style={pdfTableStyle}>
        <thead>
          <tr style={{ backgroundColor: '#f8fafc' }}>
            <th style={pdfThStyle}>S.No</th><th style={pdfThStyle}>SKU</th><th style={{ ...pdfThStyle, textAlign: 'left' }}>Item Name</th><th style={pdfThStyle}>New Qty</th><th style={pdfThStyle}>Rental Qty</th><th style={pdfThStyle}>Tick</th><th style={pdfThStyle}>Weight</th><th style={pdfThStyle}>Total Weight</th>
          </tr>
        </thead>
        <tbody>
          {mocData.components?.map((component, index) => (
            <tr key={index}>
              <td style={pdfTdStyle}>{index + 1}</td><td style={pdfTdStyle}>{component.sku}</td><td style={{ ...pdfTdStyle, textAlign: 'left' }}>{component.name}</td><td style={pdfTdStyle}>{component.newQty || 0}</td><td style={pdfTdStyle}>{component.rentalQty || 0}</td><td style={pdfTdStyle}>{renderPrintCheckbox()}</td><td style={pdfTdStyle}>{(parseFloat(component.weight || 0)).toFixed(2)} kg</td><td style={pdfTdStyle}>{((parseInt(component.newQty || 0, 10) + parseInt(component.rentalQty || 0, 10)) * parseFloat(component.weight || 0)).toFixed(2)} kg</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 'bold', backgroundColor: '#f2f2f2' }}>
            <td colSpan="3" style={{ ...pdfTdStyle, textAlign: 'right' }}>Total</td>
            <td style={pdfTdStyle}>{tableTotals.newQty}</td>
            <td style={pdfTdStyle}>{tableTotals.rentalQty}</td>
            <td style={pdfTdStyle}></td>
            <td style={pdfTdStyle}></td>
            <td style={pdfTdStyle}>{tableTotals.totalWeight.toFixed(2)} kg</td>
          </tr>
        </tbody>
      </table>

      <table style={headerTableStyle}>
        <tbody>
          <tr><td style={labelCellStyle}>Stickers</td><td style={valueCellStyle}>{mocData.materialChecklist?.stickers ? 'YES' : 'NO'}</td><td style={labelCellStyle}>Laser Marking</td><td style={valueCellStyle}>{mocData.materialChecklist?.laserMarking ? 'YES' : 'NO'}</td><td style={labelCellStyle}>Material Wrapping</td><td style={valueCellStyle}>{mocData.materialChecklist?.materialWrapping ? 'YES' : 'NO'}</td></tr>
          <tr><td style={labelCellStyle}>Instructions</td><td colSpan="5" style={{ ...valueCellStyle, minHeight: '50px' }}>{mocData.materialChecklist?.materialInstructions || 'None'}</td></tr>
        </tbody>
      </table>

      {showPostInstructionsSection && (
        <>
          <table style={headerTableStyle}>
            <tbody>
              <tr>
                <td style={labelCellStyle}>Prepared by</td><td style={valueCellStyle}>{mocData.preparedBy}</td>
                <td rowSpan="6" style={{ ...headerTableCellStyle, verticalAlign: 'top', fontSize: '11px', width: '45%', borderLeft: '2px solid #000' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '5px', textDecoration: 'underline' }}>NOTICE FOR SALES TEAM:</div>
                  Sales Team, please verify the Material Quantity Against your BOM Before Signing the MOC. Any Discrepancies in the MOC Will Not Be the Responsibility of the Operations Team, As It Has Been Prepared Exactly As Per Your BOM.
                </td>
              </tr>
              <tr><td style={labelCellStyle}>Qty Verified By (Approval)</td><td style={valueCellStyle}>{mocData.quantityVerifiedBy}</td></tr>
              <tr><td style={labelCellStyle}>Qty Verified By (Dispatch)</td><td style={valueCellStyle}>{mocData.quantityVerifiedByDispatch}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle Number</td><td style={valueCellStyle}>{mocData.vehicleNumber}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle In Time</td><td style={valueCellStyle}>{mocData.vehicleInTime}</td></tr>
              <tr><td style={labelCellStyle}>Vehicle Out Time</td><td style={valueCellStyle}>{mocData.vehicleOutTime}</td></tr>
              <tr><td style={labelCellStyle}>Date</td><td style={valueCellStyle}>{mocData.vehicleDate}</td><td style={{ ...headerTableCellStyle, textAlign: 'center', fontWeight: 'bold', fontSize: '14px', backgroundColor: '#f2f2f2' }}>SIGNATURE</td></tr>
            </tbody>
          </table>

          {showFinanceSection && (
            <table style={headerTableStyle}>
              <tbody>
                <tr><td colSpan="4" style={{ ...headerTableCellStyle, textAlign: 'center', backgroundColor: '#f1f5f9', fontWeight: '800' }}>FOR FINANCE USE ONLY</td></tr>
                <tr><td style={{ ...labelCellStyle, width: '25%' }}>Payment Terms</td><td style={{ ...valueCellStyle, width: '25%' }}>{mocData.paymentTerms}</td><td style={{ ...labelCellStyle, width: '25%' }}>Payment Status</td><td style={{ ...valueCellStyle, width: '25%' }}>{mocData.paymentStatus}</td></tr>
                <tr><td style={labelCellStyle}>Last Payment amount</td><td style={valueCellStyle}>{mocData.lastPaymentAmount}</td><td style={labelCellStyle}>Outstanding Amount</td><td style={valueCellStyle}>{mocData.outstandingAmount}</td></tr>
                <tr><td style={labelCellStyle}>Payment Rec. Date</td><td style={valueCellStyle}>{mocData.paymentRecDate}</td><td style={labelCellStyle}>Outstanding days</td><td style={valueCellStyle}>{mocData.outstandingDays}</td></tr>
                <tr><td style={labelCellStyle}>Sign/Name (Finance)</td><td style={valueCellStyle}>{mocData.approvedByFinance}</td><td style={labelCellStyle}>Salesperson sign/name</td><td style={valueCellStyle}>{mocData.approvedBySalesperson}</td></tr>
              </tbody>
            </table>
          )}

          <div style={{ marginTop: '100px', display: 'flex', justifyContent: 'space-between', padding: '0 20px', alignItems: 'flex-end' }}>
            <div style={{ ...signLineStyle, position: 'relative' }}>
              {formattedSalesManagerApprovalTime && (
                salesManagerSignatureSrc ? (
                  <img src={salesManagerSignatureSrc} alt="Sales Manager Sign" style={signImageOverlayStyle} />
                ) : (
                  <img src="/placeholder-sign.png" alt="Sales Manager Sign" style={{ ...signImageOverlayStyle, maxHeight: '50px', bottom: '35px' }} />
                )
              )}
              {!formattedSalesManagerApprovalTime && mocData.approvedBySalesperson && (
                salespersonSignatureSrc ? (
                  <img src={salespersonSignatureSrc} alt="Salesperson Sign" style={signImageOverlayStyle} />
                ) : (
                  <img src="/placeholder-sign.png" alt="Sign" style={{ ...signImageOverlayStyle, maxHeight: '50px', bottom: '35px' }} />
                )
              )}
              <div>Sales Manager</div>
              {formattedSalesManagerApprovalTime && (
                <>
                  {salesManagerName && <div style={signNameStyle}>{salesManagerName}</div>}
                  <div style={signDateStyle}>{formattedSalesManagerApprovalTime}</div>
                </>
              )}
            </div>
            <div style={{ ...signLineStyle, position: 'relative' }}>
              {mocData.approvedByFinance === 'Jasveer Singh' && (
                <img src="/Jasveer-sir-Sign.png" alt="Sign" style={signImageOverlayStyle} />
              )}
              {(mocData.approvedByFinance === 'Tikkam' || mocData.approvedByFinance === 'Tikkam Chand') && (
                <img src="/Tikkam-Sign.png" alt="Sign" style={signImageOverlayStyle} />
              )}
              {(mocData.approvedByFinance === 'Saurabh' || mocData.approvedByFinance === 'Saurabh Ganeriwal') && (
                <img src="/Saurabh-Ganeriwal-Sign.png" alt="Sign" style={signImageOverlayStyle} />
              )}
              <div>For Finance Approval</div>
              {formattedFinanceApprovalTime && (
                <>
                  {mocData.approvedByFinance && <div style={signNameStyle}>{mocData.approvedByFinance}</div>}
                  <div style={signDateStyle}>{formattedFinanceApprovalTime}</div>
                </>
              )}
            </div>
            <div style={{ ...signLineStyle, position: 'relative' }}>
              {mocData.quantityVerifiedBy === 'Ghanshyam Prajapat' && (
                <img src="/Ghansyam-Sign.png" alt="Sign" style={signImageOverlayStyle} />
              )}
              {mocData.quantityVerifiedBy === 'Rajesh Kumar' && (
                <img src="/Rajesh-Kumar-Sign.png" alt="Sign" style={signImageOverlayStyle} />
              )}
              {mocData.quantityVerifiedBy === 'Ghanshyam Prajapat' ? (
                <>
                  <div>Operations Head</div>
                  <div style={signNameStyle}>Ghanshyam Prajapat</div>
                  {formattedOpsApprovalTime && <div style={signDateStyle}>{formattedOpsApprovalTime}</div>}
                </>
              ) : (
                'Operations Head'
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const pdfContainerStyle = { backgroundColor: 'white', padding: '40px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', minHeight: '1100px', width: '100%', boxSizing: 'border-box', color: '#000' };
const headerTableStyle = { width: '100%', borderCollapse: 'collapse', marginBottom: '15px', border: '1px solid #000' };
const headerTableCellStyle = { border: '1px solid #000', padding: '8px', fontSize: '13px' };
const labelCellStyle = { ...headerTableCellStyle, fontWeight: 'bold', backgroundColor: '#f2f2f2' };
const valueCellStyle = { ...headerTableCellStyle, backgroundColor: '#fff' };
const pdfTableStyle = { width: '100%', borderCollapse: 'collapse', marginTop: '15px', border: '1px solid #000' };
const pdfThStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center', fontWeight: 'bold' };
const pdfTdStyle = { border: '1px solid #000', padding: '6px', fontSize: '12px', textAlign: 'center' };
const signLineStyle = { borderTop: '1px solid #000', width: '180px', textAlign: 'center', paddingTop: '10px', fontSize: '12px', fontWeight: 'bold' };
const signNameStyle = { marginTop: '3px', fontSize: '11px', fontWeight: 'bold', lineHeight: '1.3' };
const signDateStyle = { marginTop: '3px', fontSize: '10px', fontWeight: 'normal', lineHeight: '1.3' };
const signImageOverlayStyle = {
  position: 'absolute',
  bottom: '30px',
  left: '50%',
  transform: 'translateX(-50%)',
  maxHeight: '100px',
  pointerEvents: 'none'
};

export default MOCPreviewContent;
